import { ReactElement } from "react";
import { Box } from "@twilio-paste/core/box";
import { Heading } from "@twilio-paste/core/heading";
import { Button } from "@twilio-paste/core/button";

import {
  PromptSuggestionGroupBlock,
  dataTestIds,
  trackingDetails,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import { useWiseOwlContext } from "../../../hooks";

interface PromptSuggestionGroupProps {
  block: PromptSuggestionGroupBlock;
}

const PromptSuggestionGroup = ({
  block,
}: PromptSuggestionGroupProps): ReactElement => {
  const {
    handlers: { handleStartNewChat, handleTrackEvent },
  } = useWiseOwlContext();

  const handlePromptTrackingEvent = (prompt: string): void => {
    if (handleTrackEvent) {
      handleTrackEvent(trackingDetails.wiseOwlPromptSuggestionClicked, {
        promptSelected: prompt,
      });
    }
  };

  const handleClickPromptSuggestion = (prompt: string): void => {
    if (handleStartNewChat) {
      handleStartNewChat({ input: prompt });
    }
    handlePromptTrackingEvent(prompt);
  };

  return (
    <Box as="section" display="flex" columnGap="space40" marginTop="space50">
      {block.values.categories.map((category) => (
        <Box
          key={`category-${category.name}`}
          as="article"
          backgroundColor="colorBackgroundWarningWeakest"
          borderRadius="borderRadius30"
          marginTop="space70"
          paddingX="space70"
          paddingY="space60"
          width="size40"
          display="flex"
          flexDirection="column"
          rowGap="space40"
          data-testid={dataTestIds.wiseOwlChat.promptSuggestion.category}
        >
          <Box as="div" marginBottom="space20">
            <Heading
              as="h3"
              variant="heading30"
              data-testid={dataTestIds.wiseOwlChat.promptSuggestion.heading}
            >
              {category.name}
            </Heading>
          </Box>
          {/* onHover secondary button variant */}
          {category.suggestions.map((suggestion) => (
            <Button
              key={`suggestion-${suggestion}`}
              variant="reset"
              padding="space30"
              backgroundColor="colorBackgroundBody"
              borderColor="colorBorderDecorative10Weaker"
              borderRadius="borderRadius20"
              borderStyle="solid"
              borderWidth="borderWidth10"
              fontWeight="fontWeightBold"
              width="fit-content"
              textAlign="left"
              _hover={{
                textDecoration: "none",
                color: "colorTextLink",
                backgroundColor: "colorBackgroundBody",
                boxShadow: "shadowBorderPrimary",
              }}
              onClick={() => handleClickPromptSuggestion(suggestion)}
            >
              {suggestion}
            </Button>
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default PromptSuggestionGroup;
